import { JitsiMeeting } from "@jitsi/react-sdk";
import { useCallback, useMemo } from "react";
import { Minimize2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCallState } from "@/zustand/CallState";
import { IJitsiMeetExternalApi } from "@jitsi/react-sdk/lib/types";
import { cn } from "@/lib/utils";
import BottomSpacer from "@/components/shared/BottomSpacer";
import { useCallModerationState } from "@/zustand/CallModerationState";
import useModerationEvents from "./useModerationEvents";
import ModerationPanel from "./ModerationPanel";
import {
  buildMeetToolbarButtons,
  buildOrionCustomToolbarButtons,
  buildOrionMainToolbarButtons,
  ORION_CHAT_TOOLBAR_BUTTON_ID,
} from "./jitsiToolbar";
import { featureFlags } from "@/config/featureFlags";
import useOrionChatButtonEvent from "./useOrionChatButtonEvent";
import useModerationButtonEvent from "./useModerationButtonEvent";
import useModerationButtonVisibility from "./useModerationButtonVisibility";
import useMinimizeButtonEvent from "./useMinimizeButtonEvent";
import useRecordingConsentToast from "./useRecordingConsentToast";
import OrionChatPanel from "./OrionChatPanel";

/**
 * The EXACT Jitsi toolbar shipped at tag `v1.1.27` (before any Orion Meet UI work). Used verbatim
 * for `interfaceConfigOverwrite.TOOLBAR_BUTTONS` when the `orionMeetUi` master flag is OFF, so a
 * prod build reproduces the old in-call toolbar (incl. `participants-pane`, which the new curated
 * `buildMeetToolbarButtons` list deliberately drops).
 */
const LEGACY_TOOLBAR_BUTTONS: string[] = [
  "backgroundblur",
  "camera",
  "chat",
  "closedcaptions",
  "desktop",
  "download",
  "embedmeeting",
  "etherpad",
  "filmstrip",
  "fodeviceselection",
  "fullscreen",
  "hangup",
  "microphone",
  "participants-pane",
  "raisehand",
  "recording",
  "security",
  "settings",
  "sharedvideo",
  "shareaudio",
  "shortcuts",
  "stats",
  "tileview",
  "videoquality",
  "videobackgroundblur",
];

type Props = {
  /**
   * Visitor/webinar tier is Phase 5 and is NOT built yet — this only plumbs the flag down to
   * `ModerationPanel`'s reactions note. Defaults to `false` (no caller passes it yet), which
   * keeps every existing call's behavior provably unchanged.
   */
  isWebinarMode?: boolean;
};

export default function Call({ isWebinarMode = false }: Props) {
  const { t } = useTranslation();
  const callMinimized = useCallState((state) => state.callMinimized);
  const setCallMinimized = useCallState((state) => state.setCallMinimized);
  const setShowJitsiUI = useCallState((state) => state.setShowConferenceUI);
  const setCallInfo = useCallState((state) => state.setCallInfo);
  const setApiRef = useCallState((state) => state.setApiRef);
  const joinedCall = useCallState((state) => state.joinedCall);
  const showJitsiUI = useCallState((state) => state.showConferenceUI);
  const callInfo = useCallState((state) => state.callInfo);
  const apiRef = useCallState((state) => state.apiRef);
  const isOrionChatPanelOpen = useCallState(
    (state) => state.isOrionChatPanelOpen,
  );
  const setOrionChatPanelOpen = useCallState(
    (state) => state.setOrionChatPanelOpen,
  );
  const isModerationPanelOpen = useCallModerationState(
    (state) => state.isModerationPanelOpen,
  );
  const setModerationPanelOpen = useCallModerationState(
    (state) => state.setModerationPanelOpen,
  );

  // MASTER Orion Meet UI flag (default OFF). OFF ⇒ Call.tsx reproduces the v1.1.27 in-call
  // experience exactly (old jitsi config + floating minimize overlay, no meet-UI panels/hooks).
  const orionMeetUiEnabled = featureFlags.orionMeetUi;
  // Orion in-call chat is a SEPARATE flag (default OFF), and only has any effect when the master
  // flag is ON. A local const (rather than reading inside the memos) keeps exhaustive-deps honest.
  const orionInCallChatEnabled = featureFlags.orionInCallChat;

  // When the master flag is OFF, every meet-UI iframe hook is disabled by handing it a null api
  // (each hook early-returns on null): no moderation event wiring, no custom toolbar-button
  // listeners, no moderator-only re-apply of `customToolbarButtons`, and no recording-consent
  // toast — none of which existed at v1.1.27.
  const meetApi = orionMeetUiEnabled ? apiRef : null;

  // Keeps the roster/moderation store in sync with the iframe for as long as `apiRef` is set.
  // Deliberately NOT gated on `!callMinimized` — minimizing keeps `apiRef` alive (height:"0px"
  // below), and the moderation state (who's a moderator, who's in the room) must survive a
  // minimize just like the call itself does. `apiRef` isn't reset to null on hangup/cancel today
  // (only on logout, via `resetCallState`), but `useModerationEvents`'s effect still tears down
  // and resets moderation state correctly whenever `apiRef` changes to a NEW call's api instance
  // (each call's `onApiReady` below hands us a fresh object), so stale state never leaks across
  // calls even though the old reference lingers between them.
  useModerationEvents(meetApi);

  // Translated labels for the Orion custom toolbar buttons — shared by the initial `configOverwrite`
  // build below and the runtime re-apply in `useModerationButtonVisibility`, so both stay in sync.
  const orionToolbarLabels = useMemo(
    () => ({
      chat: t("videoCall.orionChat.togglePanel"),
      moderation: t("videoCall.moderation.panelTitle"),
      minimize: t("videoCall.minimize"),
    }),
    [t],
  );

  // Toggle the Orion chat / moderation panels when their `customToolbarButtons` entries (injected
  // via `configOverwrite` below) fire `toolbarButtonClicked`. These are the primary controls now
  // that the floating overlay buttons are gone; the moderation hook no-ops for non-moderators.
  useOrionChatButtonEvent(meetApi);
  useModerationButtonEvent(meetApi);
  useMinimizeButtonEvent(meetApi);
  // Make the moderation toolbar button moderator-only: it's in the initial config for everyone,
  // then removed at runtime for confirmed non-moderators (regression-free — see the hook).
  useModerationButtonVisibility(meetApi, orionToolbarLabels);

  // One-time "this meeting is being recorded" notice when Jitsi reports
  // recording turned ON (additive to Jitsi's own native indicator; notice-only).
  useRecordingConsentToast(meetApi);

  const getIFrameRef = useCallback((parentNode: HTMLDivElement | null) => {
    const iframe = parentNode?.querySelector("iframe");
    if (iframe && parentNode) {
      parentNode.style.width = "100%";
      parentNode.style.height = "100%";
    }
  }, []);

  // Leaving/ending the call (native OR custom hangup) fires Jitsi's `readyToClose`.
  // Tear down the Orion overlay so control returns to the chat (ChatPlayground) — Call is
  // an absolute overlay sibling of MiddleColumn, so clearing `showConferenceUI` (via
  // `setUserDisconnected`) makes this component return null and reveals the chat underneath,
  // instead of leaving the disposed iframe's post-hangup welcome page on screen. Idempotent
  // with the custom-hangup path (which already calls `setUserDisconnected`).
  //
  // STALE-FIRE GUARD: `@jitsi/react-sdk` wires the `readyToClose` listener ONCE (its
  // `loadIFrame` effect is guarded by `!apiRef.current`) and never disposes the external API
  // on unmount/roomName change. So when a call is left and a NEW call starts (hangup →
  // Call returns null → JitsiMeeting unmounts, then startCall mounts a fresh room), the OLD
  // call's leaked api keeps its `readyToClose` bound to THIS handler instance. A late
  // `readyToClose` from that old api must NOT tear down the newly-started call. We capture the
  // call id this handler was wired for (`wiredCallId`) and no-op unless it's still the current
  // call. Each mount gets a handler bound to its own call id (via the `useCallback` dep), so
  // the current call's handler still fires normally while a stale one from a prior call is
  // ignored. Also idempotent: after a custom hangup cleared `callInfo`, the mismatch no-ops.
  const wiredCallId = callInfo?.currentCallId;
  const handleReadyToClose = useCallback(() => {
    const state = useCallState.getState();
    if (!wiredCallId || state.callInfo?.currentCallId !== wiredCallId) {
      return;
    }
    state.setCallEnding(false);
    state.setUserDisconnected();
  }, [wiredCallId]);

  const configOverwrite = useMemo(
    () => ({
      subject: callInfo?.subject,
      enableNoisyMicDetection: true,
      enableNoAudioDetection: true,
      disableInviteFunctions: true, // Disables the invite feature
      disableProfile: true, // Prevents users from changing name/avatar
      // ── Orion Meet in-call UI overrides — gated by the master flag ──
      // When `orionMeetUi` is OFF the spread below is empty, so `configOverwrite` is EXACTLY the
      // five keys above == the v1.1.27 config (no videoQuality / customToolbarButtons /
      // mainToolbarButtons). When ON, the current Orion Meet behavior applies.
      ...(orionMeetUiEnabled
        ? {
            // #1 DEFAULT receive video-quality bump. `videoQuality` is in jitsi's `configWhitelist`
            // (so it IS honored through the iframe — verified against stable/jitsi-meet_11031
            // `configWhitelist.ts` + the deployed v=9268 bundle). `minHeightForQualityLvl: { 0: "high" }`
            // makes the client REQUEST the HIGH (720p) tier for received video on first load regardless
            // of rendered tile height, instead of jitsi's tile-view default of STANDARD (360p). (1:1 /
            // stage view already default to HIGH/ULTRA.) This sets the FIRST-LOAD DEFAULT only — the
            // in-call video-quality slider still overrides it per user (that pref is separate Redux state).
            //
            // Deliberately CONSERVATIVE on the stability tradeoff: jitsi's `maxFullResolutionParticipants`
            // guard (default 2) is LEFT at its default so large group tile views still auto-downgrade —
            // we do NOT force it to -1 (that would ask every tile for 720p and risk congesting big calls).
            // "Keep video visible at very poor bandwidth (don't go audio-only)" is NOT shipped here: the
            // old `enableLayerSuspension` config key was removed from jitsi in 2020 (no-op, not
            // whitelisted) and stream suspension under real bandwidth scarcity is bridge-side (JVB), not
            // client-configurable. That part needs a live constrained-bandwidth test — see PR notes.
            videoQuality: { minHeightForQualityLvl: { 0: "high" } },
            // Native in-toolbar Orion Moderation + Minimize buttons (replaces the old floating overlay),
            // plus the Orion Chat button only when the in-call-chat flag is ON.
            // `customToolbarButtons` is a `config` key (NOT interfaceConfig) and is accepted via the
            // iframe config on our deployed build — see jitsiToolbar.ts. Clicks arrive as
            // `toolbarButtonClicked` and are handled by the hooks above.
            customToolbarButtons: buildOrionCustomToolbarButtons(
              orionToolbarLabels,
              { includeChat: orionInCallChatEnabled },
            ),
            // Pin the Orion buttons in the MAIN toolbar and demote videoquality/fullscreen/security to
            // the ⋯ overflow. WITHOUT this, our custom buttons sort last into the overflow. This is a
            // FRAGILE, version-coupled mechanism (keyed by button-count against jitsi's width THRESHOLDS)
            // — see jitsiToolbar.ts + docs/jitsi/version-bump-todo.md and RE-VERIFY on any image bump.
            // With the chat flag OFF, the chat slot is the native `chat` id (kept enabled below) so the
            // demoted natives stay in overflow. Guest embeds deliberately omit this.
            mainToolbarButtons: buildOrionMainToolbarButtons(
              orionInCallChatEnabled ? ORION_CHAT_TOOLBAR_BUTTON_ID : "chat",
            ),
          }
        : {}),
    }),
    [
      callInfo?.subject,
      orionToolbarLabels,
      orionInCallChatEnabled,
      orionMeetUiEnabled,
    ],
  );
  const interfaceConfigOverwrite = useMemo(
    // When the master flag is OFF, use the EXACT v1.1.27 toolbar (`LEGACY_TOOLBAR_BUTTONS`). When
    // ON, use the curated toolbar shared with the guest embed — see jitsiToolbar.ts. In the ON path,
    // if the Orion in-call chat flag is OFF we add Jitsi's native `chat` back so calls keep a chat.
    () => ({
      TOOLBAR_BUTTONS: orionMeetUiEnabled
        ? buildMeetToolbarButtons(!orionInCallChatEnabled)
        : LEGACY_TOOLBAR_BUTTONS,
    }),
    [orionInCallChatEnabled, orionMeetUiEnabled],
  );

  if (!showJitsiUI || callInfo === null) {
    return null;
  }

  const { currentCallId, token } = callInfo;

  function handleCancelCall() {
    setCallInfo(null);
    setShowJitsiUI(false);
  }

  return (
    <div
      style={{
        position: "absolute",
        zIndex: 50,
        background: "black",
        width: "100%",
        // Explicit dynamic-viewport height (self-documenting). This overlay is
        // position:absolute with no inset, so its containing block is #Main
        // (ChatLayout, `fixed ... h-dvh`); "100%" already resolves to that h-dvh,
        // so 100dvh is equivalent-and-explicit, not a behavior change. NOTE: this
        // does NOT by itself resolve the #1078 strip — that originates inside the
        // Jitsi iframe (see PR notes). The PWA home-indicator spacer is handled by
        // <BottomSpacer/> below (gated on IS_IOS && IS_PWA).
        // Subtract the top safe-area inset: #Main pads the notch area, and this
        // overlay's static position starts below that padding — a full 100dvh
        // would push the Jitsi toolbar off-screen on notched iOS PWAs.
        height: callMinimized
          ? "0px"
          : "calc(100dvh - env(safe-area-inset-top, 0px))",
        overflow: "hidden",
      }}
      className="flex flex-col"
    >
      {orionMeetUiEnabled ? (
        /* ORION MEET UI ON: the chat + moderation + minimize toggles all live INSIDE the Jitsi
           toolbar (via `configOverwrite.customToolbarButtons`). The floating minimize overlay was
           removed; minimizing now collapses the iframe to height:0 (below), which hides its own
           toolbar — so RESTORE stays an overlay (the Maximize2 pill in `CallButtons.tsx`), not a
           toolbar button. The only overlay left here is Cancel (X), shown pre-join to back out
           before a conference (and therefore its toolbar) exists. */
        !joinedCall && (
          <div className="absolute m-2 flex items-center gap-2 rounded-sm border border-primary/80 hover:border-destructive/80">
            <X
              className="size-8 cursor-pointer text-white hover:bg-destructive/20 hover:text-destructive"
              onClick={handleCancelCall}
            />
          </div>
        )
      ) : (
        /* ORION MEET UI OFF (== v1.1.27): the floating overlay carries the Minimize button (while
           in-call) and the Cancel (X) button (pre-join). Minimize/moderation/chat are NOT in the
           Jitsi toolbar. */
        <div
          className={cn(
            "absolute m-2 flex items-center gap-2 rounded-sm border border-primary/80",
            joinedCall && !callMinimized ? "" : "hover:border-destructive/80",
          )}
        >
          {joinedCall && !callMinimized && (
            <Minimize2
              className="size-8 cursor-pointer text-white hover:bg-primary/50"
              onClick={() => setCallMinimized(true)}
            />
          )}
          {!joinedCall && (
            <X
              className="size-8 cursor-pointer text-white hover:bg-destructive/20 hover:text-destructive"
              onClick={handleCancelCall}
            />
          )}
        </div>
      )}
      {orionMeetUiEnabled && isModerationPanelOpen && (
        <ModerationPanel
          onClose={() => setModerationPanelOpen(false)}
          isWebinarMode={isWebinarMode}
        />
      )}
      {orionMeetUiEnabled && orionInCallChatEnabled && isOrionChatPanelOpen && (
        <OrionChatPanel onClose={() => setOrionChatPanelOpen(false)} />
      )}
      <JitsiMeeting
        domain={
          import.meta.env.VITE_MEET_DOMAIN || "meet.test.orionmessenger.io"
        }
        roomName={currentCallId}
        jwt={token}
        configOverwrite={configOverwrite}
        interfaceConfigOverwrite={interfaceConfigOverwrite}
        getIFrameRef={getIFrameRef}
        onApiReady={(api: IJitsiMeetExternalApi) => {
          setApiRef(api);
        }}
        // v1.1.27 passed NO `onReadyToClose` prop at all — the return-to-chat teardown is meet-UI
        // work. Conditionally SPREAD the prop (rather than passing `undefined`) so that when the
        // master flag is OFF the prop is entirely absent, exactly matching the v1.1.27 prop shape.
        {...(orionMeetUiEnabled ? { onReadyToClose: handleReadyToClose } : {})}
      />
      <BottomSpacer />
    </div>
  );
}
